import { supabase } from '@/lib/supabase';
import type { FirmRow, FirmRole } from '@/types/database';
import type { PublicFirmBrand } from '@/lib/host-branding';

export interface MyMembership {
  role: FirmRole;
  firm: Pick<FirmRow, 'id' | 'name' | 'slug' | 'status' | 'seat_limit'>;
}

/**
 * The signed-in person's firm and role, or null if they belong to none.
 *
 * Two plain queries rather than an embedded join: both tables are read-only
 * to the browser, and the rules let a person see their own membership row and
 * their own firm (even a suspended one, so the app can say why it's empty).
 */
export async function fetchMyMembership(): Promise<MyMembership | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data: member, error } = await supabase
    .from('firm_members')
    .select('firm_id, role')
    .eq('user_id', user.id)
    .maybeSingle();
  if (error) throw error;
  if (!member) return null;

  const { data: firm, error: firmError } = await supabase
    .from('firms')
    .select('id, name, slug, status, seat_limit')
    .eq('id', member.firm_id)
    .maybeSingle();
  if (firmError) throw firmError;
  if (!firm) return null;

  return { role: member.role, firm };
}

/**
 * Whether the signed-in person is a platform admin (0033). Asked of the
 * database, which answers only about the caller.
 */
export async function amIPlatformAdmin(): Promise<boolean> {
  const { data, error } = await supabase.rpc('is_platform_admin');
  if (error) throw error;
  return data === true;
}

/**
 * A firm's public face by its short name (0037) — for the sign-in page on its
 * own address. Null when no firm uses that name. Anonymous.
 */
export async function fetchPublicFirmBrand(slug: string): Promise<PublicFirmBrand | null> {
  const { data, error } = await supabase.rpc('public_firm_brand', { p_slug: slug });
  if (error) throw error;
  return (data as PublicFirmBrand | null) ?? null;
}
